/**
 * Pure waiver requirement and variant rules, shared by Angular and Cloud
 * Functions.
 *
 * SHARED SOURCE — this file, `waiver-compliance.ts` and `envelope.model.ts`
 * are compiled to CommonJS in `functions/shared/waiver/` by
 * `npm run build:waiver-shared`. Keep them framework-free: no Angular, no
 * Firebase, no imports outside these files. `npm run check:waiver-shared`
 * (run in CI) fails when the compiled copy is stale.
 *
 * Callers load the data (Firestore, caches); these functions only decide.
 * Input types are structural so callers pass their own full models.
 */

/** The waiver fields the rules read. */
export interface WaiverRuleWaiver {
  id?: string;
  name?: string;
  /** Explicit group this waiver is a variant of, if any. */
  groupId?: string;
}

/** Minor-age threshold when neither the company nor its waivers set one. */
export const DEFAULT_MINOR_AGE_THRESHOLD = 18;

/** A usable threshold value: a finite positive number. */
function isUsableThreshold(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * The company threshold implied by its waivers' legacy per-waiver
 * `minorAgeThreshold` values — the most common one, ties going to the
 * higher value. Undefined when no waiver carries one. Seeds a company that
 * predates the company-wide setting.
 */
export function minorAgeThresholdSeed(waivers: ReadonlyArray<{ minorAgeThreshold?: unknown }>): number | undefined {
  const counts = new Map<number, number>();
  for (const waiver of waivers) {
    const raw = waiver?.minorAgeThreshold;
    if (!isUsableThreshold(raw)) continue;
    counts.set(raw, (counts.get(raw) ?? 0) + 1);
  }
  if (counts.size === 0) return undefined;
  let best: number | undefined;
  let bestCount = 0;
  for (const [value, count] of counts) {
    if (count > bestCount || (count === bestCount && (best === undefined || value > best))) {
      best = value;
      bestCount = count;
    }
  }
  return best;
}

/**
 * The company's minor-age threshold: `companies/{id}.waiverMinorAgeThreshold`
 * when usable, else the waiver seed, else {@link DEFAULT_MINOR_AGE_THRESHOLD}.
 */
export function resolveMinorAgeThreshold(
  companyValue: unknown,
  waivers: ReadonlyArray<{ minorAgeThreshold?: unknown }>,
): number {
  if (isUsableThreshold(companyValue)) return companyValue;
  return minorAgeThresholdSeed(waivers) ?? DEFAULT_MINOR_AGE_THRESHOLD;
}

/** The group fields the rules read. */
export interface WaiverRuleGroup {
  id: string;
  name?: string;
  waiverIds: string[];
  activeWaiverId: string;
  minorWaiverId?: string | null;
}

/** The product fields the rules read. */
export interface WaiverRuleProduct {
  waivers?: unknown;
  productGroupID?: unknown;
}

/**
 * Picks the variant of `group` a participant signs.
 *
 * Only returns a variant id that resolves to a known waiver. The group doc
 * may briefly carry a dangling `activeWaiverId` / `minorWaiverId` (e.g.
 * right after a waiver delete), so it falls back:
 *   minor dangling  → try active
 *   active dangling → first member of `waiverIds` that resolves
 *   nothing resolves → `activeWaiverId` as-is
 */
export function pickWaiverVariant(
  group: WaiverRuleGroup,
  isMinor: boolean,
  waivers: ReadonlyArray<WaiverRuleWaiver>,
): string {
  const resolves = (id: string | null | undefined): boolean =>
    !!id && waivers.some(w => w.id === id);

  if (isMinor && group.minorWaiverId && resolves(group.minorWaiverId)) {
    return group.minorWaiverId;
  }
  if (resolves(group.activeWaiverId)) return group.activeWaiverId;
  for (const id of group.waiverIds) {
    if (resolves(id)) return id;
  }
  return group.activeWaiverId;
}

/**
 * Resolves a product-attached waiver reference to the waiver a participant
 * signs:
 *
 *   1. A waiver with a `groupId` whose group exists → that group's variant.
 *   2. A waiver with no `groupId` (or a dangling one) is an implicit solo
 *      group → the waiver itself.
 *   3. Not a waiver but an explicit group id → that group's variant.
 *   4. Unknown → undefined.
 *
 * `groups` is the EXPLICIT group list; implicit solo groups are never
 * looked up here (step 2 covers them).
 */
export function resolveWaiverRefForParticipant(
  waiverRefId: string,
  isMinor: boolean,
  waivers: ReadonlyArray<WaiverRuleWaiver>,
  groups: ReadonlyArray<WaiverRuleGroup>,
): string | undefined {
  const waiver = waivers.find(w => w.id === waiverRefId);
  if (waiver?.groupId) {
    const group = groups.find(g => g.id === waiver.groupId);
    if (group) return pickWaiverVariant(group, isMinor, waivers);
  }
  if (waiver) return waiver.id;
  const group = groups.find(g => g.id === waiverRefId);
  if (group) return pickWaiverVariant(group, isMinor, waivers);
  return undefined;
}

/**
 * Product-group ids whose `waivers[]` must be loaded for these products,
 * deduped in first-seen order.
 */
export function requiredProductGroupIds(products: ReadonlyArray<WaiverRuleProduct | null | undefined>): string[] {
  const ids = new Set<string>();
  for (const product of products) {
    if (product && typeof product.productGroupID === 'string' && product.productGroupID) {
      ids.add(product.productGroupID);
    }
  }
  return Array.from(ids);
}

/**
 * Every waiver reference attached to a booking's products, deduped in
 * order: each product's own `waivers[]` first (the pre-group bridge), then
 * each loaded product group's `waivers[]`.
 *
 * `productGroupWaivers` holds the loaded groups' `waivers` values in
 * `requiredProductGroupIds` order; missing groups or non-array values are
 * skipped. Product-level entries are taken as stored; group-level entries
 * must be non-empty strings.
 */
export function collectRequiredWaiverRefIds(
  products: ReadonlyArray<WaiverRuleProduct | null | undefined>,
  productGroupWaivers: ReadonlyArray<unknown>,
): string[] {
  const ids = new Set<string>();
  for (const product of products) {
    if (!product) continue;
    if (Array.isArray(product.waivers)) {
      for (const id of product.waivers) ids.add(id);
    }
  }
  for (const waivers of productGroupWaivers) {
    if (!Array.isArray(waivers)) continue;
    for (const id of waivers) {
      if (typeof id === 'string' && id) ids.add(id);
    }
  }
  return Array.from(ids);
}

/** How a required reference is satisfied. */
export type RequiredWaiverRefClassification<W extends WaiverRuleWaiver, G extends WaiverRuleGroup> =
  /** A builder waiver id (possibly a variant inside a group). */
  | { kind: 'builder'; id: string; name: string; waiver: W }
  /** An explicit group id; resolved per participant at envelope time. */
  | { kind: 'group'; id: string; name: string; group: G; activeVariant?: W }
  /**
   * Neither — a legacy `companies/{id}/waivers` doc or unknown. Callers
   * with Firestore access look the legacy doc up; either way it is not an
   * envelope requirement.
   */
  | { kind: 'other'; id: string };

/**
 * Classifies one required reference against the company's builder waivers
 * and EXPLICIT groups. Builder waivers win over groups.
 */
export function classifyRequiredWaiverRef<W extends WaiverRuleWaiver, G extends WaiverRuleGroup>(
  id: string,
  waivers: ReadonlyArray<W>,
  groups: ReadonlyArray<G>,
): RequiredWaiverRefClassification<W, G> {
  const waiver = waivers.find(w => w.id === id);
  if (waiver) {
    return { kind: 'builder', id, name: waiver.name, waiver };
  }
  const group = groups.find(g => g.id === id);
  if (group) {
    const activeVariant = waivers.find(w => w.id === group.activeWaiverId);
    return { kind: 'group', id, name: group.name || activeVariant?.name || 'Waiver', group, activeVariant };
  }
  return { kind: 'other', id };
}

// ---------------------------------------------------------------------------
// Submission validation

/** The waiver field properties submission validation reads. */
export interface WaiverSubmissionField {
  id: string;
  type: string;
  required?: boolean;
}

/**
 * Trusted context for a submission. Callers derive it themselves — never
 * from the submitting client.
 */
export interface WaiverSubmissionOptions {
  /**
   * The signer's classification for a signature taken now: `true` minor,
   * `false` adult, `null` unknown (an anonymous or walk-in signer). Same
   * meaning as the sign forms' `signerIsMinor`: guardian fields are
   * required for a minor, optional when unknown, and must be left empty
   * by an adult.
   */
  signerIsMinor: boolean | null;
}

export type WaiverSubmissionFieldProblem =
  /** A required value is missing or blank. */
  | 'required'
  | 'invalid-email'
  | 'invalid-phone'
  /** A signature value that is not a PNG data URL. */
  | 'invalid-signature'
  /** A value of the wrong type (every input is a string). */
  | 'invalid-value'
  /** A guardian value from a signer known to be an adult. */
  | 'not-allowed';

export type WaiverSubmissionStructuralProblem =
  | 'malformed-options'
  | 'malformed-fields'
  | 'unsupported-field-type'
  | 'malformed-form-data'
  /** A form value keyed by an id that is not one of the waiver's fields. */
  | 'unknown-field';

export interface WaiverSubmissionResult {
  valid: boolean;
  /** Set when the input could not be evaluated field by field at all. */
  structuralProblem: WaiverSubmissionStructuralProblem | null;
  /** In field order; one entry per invalid field. */
  fieldProblems: Array<{ fieldId: string; problem: WaiverSubmissionFieldProblem }>;
  /** Whether a guardian signature was given — an observable fact, as the
   *  forms record `signedByGuardian`, never a client declaration. */
  guardianSigned: boolean;
}

/** Every field type a sign form can carry. `isUnder18` is retired but
 *  still present on older waivers; like `textBody` it is display-only. */
const WAIVER_SUBMISSION_FIELD_TYPES = new Set([
  'name', 'email', 'phone', 'textBody', 'isUnder18', 'signature', 'guardianName', 'guardianSignature',
]);
const DISPLAY_ONLY_FIELD_TYPES = new Set(['textBody', 'isUnder18']);
const SUBMISSION_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** What SignaturePadDirective emits: `canvas.toDataURL('image/png')`. */
const SIGNATURE_DATA_URL_PATTERN = /^data:image\/png;base64,[A-Za-z0-9+/]+={0,2}$/;

/**
 * Validates a sign-form submission against the waiver's own fields.
 *
 * Field rules are the ones both sign forms apply today
 * (`waiver-sign.component.ts` and `in-person-sign-modal.component.ts`
 * `validateForm`):
 *   - `textBody` / `isUnder18`: display only, never validated.
 *   - `signature`: always required, whatever the builder's flag says.
 *   - `guardianSignature` / `guardianName`: required for a minor, otherwise
 *     optional; the builder's flag is ignored.
 *   - `phone`: always optional; a value needs at least 10 digits.
 *   - `email`: required when flagged; a value must look like an address.
 *   - anything else (`name`): required when flagged.
 *
 * Stricter than the forms in what it accepts as a value — this is the
 * server's check, where the forms' input widgets cannot be assumed:
 *   - every value must be a string; a missing key counts as blank;
 *   - a signature must be a PNG data URL, not merely truthy;
 *   - an adult may not supply guardian values;
 *   - keys that are not one of the waiver's field ids are refused, so
 *     nothing can stand in for a required field or ride along into the
 *     stored record.
 *
 * Pure and deterministic.
 */
export function validateWaiverSubmission(
  fields: ReadonlyArray<WaiverSubmissionField>,
  formData: unknown,
  options: WaiverSubmissionOptions,
): WaiverSubmissionResult {
  const fail = (structuralProblem: WaiverSubmissionStructuralProblem): WaiverSubmissionResult =>
    ({ valid: false, structuralProblem, fieldProblems: [], guardianSigned: false });

  const signerIsMinor = options ? options.signerIsMinor : undefined;
  if (signerIsMinor !== true && signerIsMinor !== false && signerIsMinor !== null) return fail('malformed-options');

  if (!Array.isArray(fields)) return fail('malformed-fields');
  const fieldIds = new Set<string>();
  for (const field of fields) {
    if (!field || typeof field !== 'object' || typeof field.id !== 'string' || !field.id || fieldIds.has(field.id)) {
      return fail('malformed-fields');
    }
    if (typeof field.type !== 'string' || !WAIVER_SUBMISSION_FIELD_TYPES.has(field.type)) {
      return fail('unsupported-field-type');
    }
    fieldIds.add(field.id);
  }

  if (!formData || typeof formData !== 'object' || Array.isArray(formData)) return fail('malformed-form-data');
  const data = formData as Record<string, unknown>;
  for (const key of Object.keys(data)) {
    if (!fieldIds.has(key)) return fail('unknown-field');
  }

  const guardianRequired = signerIsMinor === true;
  const guardianAllowed = signerIsMinor !== false;
  const fieldProblems: WaiverSubmissionResult['fieldProblems'] = [];
  let guardianSigned = false;

  for (const field of fields) {
    if (DISPLAY_ONLY_FIELD_TYPES.has(field.type)) continue;

    const raw = data[field.id];
    if (raw !== undefined && typeof raw !== 'string') {
      fieldProblems.push({ fieldId: field.id, problem: 'invalid-value' });
      continue;
    }
    const value = typeof raw === 'string' ? raw : '';
    const trimmed = value.trim();
    const problem = (() => {
      switch (field.type) {
        case 'signature':
          if (!value) return 'required';
          return SIGNATURE_DATA_URL_PATTERN.test(value) ? null : 'invalid-signature';
        case 'guardianSignature':
          if (!value) return guardianRequired ? 'required' : null;
          if (!guardianAllowed) return 'not-allowed';
          return SIGNATURE_DATA_URL_PATTERN.test(value) ? null : 'invalid-signature';
        case 'guardianName':
          if (!trimmed) return guardianRequired ? 'required' : null;
          return guardianAllowed ? null : 'not-allowed';
        case 'phone':
          return trimmed && trimmed.replace(/\D/g, '').length < 10 ? 'invalid-phone' : null;
        case 'email':
          if (!trimmed) return field.required ? 'required' : null;
          return SUBMISSION_EMAIL_PATTERN.test(trimmed) ? null : 'invalid-email';
        default:
          return field.required && !trimmed ? 'required' : null;
      }
    })() as WaiverSubmissionFieldProblem | null;

    if (problem) fieldProblems.push({ fieldId: field.id, problem });
    else if (field.type === 'guardianSignature' && value) guardianSigned = true;
  }

  return { valid: fieldProblems.length === 0, structuralProblem: null, fieldProblems, guardianSigned };
}

export type WaiverSigningUnavailableReason =
  /** The fields are not a valid sign form (see validateWaiverSubmission's structural problems). */
  | 'invalid-fields'
  /** No `signature` field for a signer who signs for themselves. */
  | 'signature-field-missing'
  /** A minor's waiver without both `guardianName` and `guardianSignature`. */
  | 'guardian-fields-missing';

export type WaiverSigningReadiness =
  | { signable: true }
  | { signable: false; reason: WaiverSigningUnavailableReason };

/**
 * Whether a waiver's fields can capture THIS signer's signature at all —
 * a static property of the form, independent of anything typed into it.
 *
 *   - A minor (`signerIsMinor: true`) is signed for by a guardian, whose
 *     identity is the `guardianName` field and whose signature is the
 *     `guardianSignature` field; the builder only ever adds the two
 *     together. Both must be present.
 *   - An adult (`false`) signs for themselves: a `signature` field must be
 *     present (guardian fields are not theirs to use).
 *   - Unknown (`null`): either way of signing will do.
 *
 * A form that fails validateWaiverSubmission's structural checks (or
 * malformed options) is never signable. Pure.
 */
export function waiverSigningReadiness(
  fields: ReadonlyArray<WaiverSubmissionField>,
  options: WaiverSubmissionOptions,
): WaiverSigningReadiness {
  const structure = validateWaiverSubmission(fields, {}, options);
  if (structure.structuralProblem) return { signable: false, reason: 'invalid-fields' };

  const has = (type: string) => fields.some(f => f.type === type);
  const guardianPair = has('guardianName') && has('guardianSignature');
  const ownSignature = has('signature');

  if (options.signerIsMinor === true) {
    return guardianPair ? { signable: true } : { signable: false, reason: 'guardian-fields-missing' };
  }
  if (options.signerIsMinor === false) {
    return ownSignature ? { signable: true } : { signable: false, reason: 'signature-field-missing' };
  }
  return ownSignature || guardianPair ? { signable: true } : { signable: false, reason: 'signature-field-missing' };
}
